import React from 'react';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from './ui/alert-dialog';
import { Trash2, Loader2 } from 'lucide-react';

/**
 * Branded confirmation modal for destructive actions.
 *
 * Replaces native `window.confirm()` calls so destructive flows match the
 * Guardian visual language and can show a "what else will happen" footnote
 * (e.g. "Linked defects will be unlinked").
 *
 * Usage:
 *   const [confirm, setConfirm] = useState(null);
 *   <ConfirmDeleteDialog
 *     open={!!confirm}
 *     onOpenChange={(o) => { if (!o) setConfirm(null); }}
 *     title="Delete defect?"
 *     description={`"${defect.title}" will be permanently removed.`}
 *     footnote="This cannot be undone."
 *     onConfirm={confirm?.onConfirm}
 *   />
 */
export const ConfirmDeleteDialog = ({
  open,
  onOpenChange,
  title = 'Delete this item?',
  description = 'This action cannot be undone.',
  footnote = null,
  confirmLabel = 'Delete',
  cancelLabel = 'Cancel',
  loading = false,
  onConfirm,
  testId = 'confirm-delete-dialog',
}) => (
  <AlertDialog open={open} onOpenChange={onOpenChange}>
    <AlertDialogContent data-testid={testId}>
      <AlertDialogHeader>
        <AlertDialogTitle className="flex items-center gap-2">
          <Trash2 className="w-4 h-4 text-destructive" />
          {title}
        </AlertDialogTitle>
        <AlertDialogDescription>{description}</AlertDialogDescription>
      </AlertDialogHeader>
      {footnote && (
        <p className="text-xs text-muted-foreground border-l-2 border-destructive/40 pl-3">
          {footnote}
        </p>
      )}
      <AlertDialogFooter>
        <AlertDialogCancel disabled={loading} data-testid={`${testId}-cancel`}>
          {cancelLabel}
        </AlertDialogCancel>
        <AlertDialogAction
          onClick={onConfirm}
          disabled={loading}
          data-testid={`${testId}-confirm`}
          className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
        >
          {loading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Trash2 className="w-4 h-4 mr-2" />}
          {confirmLabel}
        </AlertDialogAction>
      </AlertDialogFooter>
    </AlertDialogContent>
  </AlertDialog>
);

export default ConfirmDeleteDialog;
